import { Annotation, AnnotationSource } from "@/types/annotations";
import { Point } from "../types";
import { mergeHiddenReasons } from "../Segmentation/helpers";
import { KeyPointSource, ParsedKeypointAnnotation } from "./types";
import { isPointInsideMeasurementBoundary } from "@shared/measurementBoundary";

const SENSITIVITY_FILTER_KEYS: ReadonlySet<string> = new Set(["sensitivity"]);

export const isInsideMeasureBoundary = (measureBoundaryPixels: Point[], point: Point): boolean => {
  if (measureBoundaryPixels.length === 0) return true;
  if (measureBoundaryPixels.length < 3) return false;
  return isPointInsideMeasurementBoundary(measureBoundaryPixels, point);
};

export const getKeyPointSource = (rawSource: AnnotationSource): KeyPointSource => {
  if ("humanAnnotated" in rawSource) return "user";
  return "model";
};

export const getKeyPointColor = (source: KeyPointSource) => {
  if (source === "user") return "#FFA500";
  return "#00FF00";
};

/**
 * Parse every stored keypoint annotation into editor state, including hidden points needed for
 * threshold restoration and manual re-enabling. This is not a visible-only list: callers that
 * render or count points must filter on `keypoint.visibility` (for example, `visibleAnnotations`).
 */
export const parseKeypointAnnotations = (list: Annotation[]): ParsedKeypointAnnotation[] =>
  list
    .filter(({ keypoints }) => Array.isArray(keypoints) && keypoints.length > 0)
    .map(({ id, keypoints, source: rawSource, confidence, userVisibility }) => {
      const source = getKeyPointSource(rawSource);
      const keypoint = keypoints[0];
      const normalizedUserVisibility = {
        isVisible: userVisibility?.isVisible ?? keypoint.visibility !== 0,
        ...(userVisibility?.manualOverride !== undefined ? { manualOverride: userVisibility.manualOverride } : {}),
        ...(userVisibility?.hiddenReasons?.length ? { hiddenReasons: [...userVisibility.hiddenReasons] } : {}),
      };
      const hiddenReasons = normalizedUserVisibility.hiddenReasons ?? [];
      const hiddenReason =
        normalizedUserVisibility.isVisible === false && hiddenReasons.length === 1 ? hiddenReasons[0] : undefined;
      const visibleVisibility =
        normalizedUserVisibility.isVisible === false && keypoint.visibility !== 0 ? keypoint.visibility : undefined;
      return {
        id,
        keypoint,
        source,
        color: getKeyPointColor(source),
        confidence,
        userVisibility: normalizedUserVisibility,
        ...(hiddenReason ? { hiddenReason } : {}),
        ...(visibleVisibility !== undefined ? { visibleVisibility } : {}),
      };
    });

/**
 * Recompute the confidence filter's structured visibility without destroying
 * COCO visibility 1/2. Lowering the threshold removes a stale managed reason;
 * unrelated system reasons remain. This is computed filter state, not a
 * per-object reviewer override: existing manual overrides are exempt, and the
 * slider never mints one.
 */
export function applyConfidenceThresholdToKeypointAnnotations(
  parsedAnnotations: ParsedKeypointAnnotation[],
  threshold: number
): { changed: boolean; annotations: ParsedKeypointAnnotation[] } {
  const annotations = parsedAnnotations.map((annotation) => {
    if (annotation.userVisibility.manualOverride === true) return annotation;

    const existingReasons = annotation.userVisibility.hiddenReasons ?? [];
    // iOS currently records collector-hidden keypoints with userToggledOff but
    // without manualOverride. The confidence filter must still preserve that
    // explicit field decision.
    if (existingReasons.includes("userToggledOff")) return annotation;
    const hasManagedReason = existingReasons.includes("confidenceBelowThreshold");
    if (annotation.confidence === undefined && !hasManagedReason) return annotation;
    // Numeric-zero legacy hides with no provenance may have been explicit user
    // choices. Do not reinterpret them as confidence state.
    if (
      annotation.keypoint.visibility === 0 &&
      annotation.userVisibility.isVisible === false &&
      existingReasons.length === 0
    ) {
      return annotation;
    }

    const filterReasons =
      annotation.confidence !== undefined && annotation.confidence < threshold
        ? (["confidenceBelowThreshold"] as const)
        : [];
    const hiddenReasons = mergeHiddenReasons(existingReasons, [...filterReasons], SENSITIVITY_FILTER_KEYS);
    const isVisible = hiddenReasons.length === 0;
    const keypoint =
      isVisible && annotation.keypoint.visibility === 0 && hasManagedReason
        ? { ...annotation.keypoint, visibility: annotation.visibleVisibility ?? 2 }
        : annotation.keypoint;
    const userVisibility = {
      isVisible,
      ...(annotation.userVisibility.manualOverride !== undefined
        ? { manualOverride: annotation.userVisibility.manualOverride }
        : {}),
      ...(hiddenReasons.length > 0 ? { hiddenReasons } : {}),
    };
    const sameReasons =
      existingReasons.length === hiddenReasons.length &&
      existingReasons.every((reason, index) => reason === hiddenReasons[index]);
    if (
      keypoint === annotation.keypoint &&
      annotation.userVisibility.isVisible === userVisibility.isVisible &&
      annotation.userVisibility.manualOverride === userVisibility.manualOverride &&
      sameReasons
    ) {
      return annotation;
    }
    const hiddenReason = !isVisible && hiddenReasons.length === 1 ? hiddenReasons[0] : undefined;
    const visibleVisibility = !isVisible && keypoint.visibility !== 0 ? keypoint.visibility : undefined;
    return { ...annotation, keypoint, userVisibility, hiddenReason, visibleVisibility };
  });
  const changed = annotations.some((annotation, index) => annotation !== parsedAnnotations[index]);
  return changed ? { changed: true, annotations } : { changed: false, annotations: parsedAnnotations };
}

/**
 * A keypoint paired with its id and precomputed DISPLAY-space center. The editor
 * precomputes these once per visible-keypoint list so per-event hit-testing is a
 * tight, allocation-free scan.
 */
export interface KeypointCandidate {
  id: string;
  displayX: number;
  displayY: number;
}

/**
 * Find the visible keypoint whose center is closest to the point, within an
 * effective radius (image/display space). Mirrors the iOS AR editor's radius
 * hit-testing. The point and the candidate centers must be in the same
 * (display) coordinate space. Returns the keypoint id, or null if none is within
 * the radius. Ties resolve to the first candidate in list order.
 */
export function findNearestKeypointId(
  point: Point,
  candidates: KeypointCandidate[],
  effectiveRadius: number
): string | null {
  const maxDistanceSq = effectiveRadius * effectiveRadius;
  let bestId: string | null = null;
  let bestDistanceSq = Infinity;
  for (const candidate of candidates) {
    const dx = candidate.displayX - point.x;
    const dy = candidate.displayY - point.y;
    const distanceSq = dx * dx + dy * dy;
    if (distanceSq <= maxDistanceSq && distanceSq < bestDistanceSq) {
      bestDistanceSq = distanceSq;
      bestId = candidate.id;
    }
  }
  return bestId;
}

/** Transform a point from raw image coordinates to display coordinates. */
export const transformPoint = (
  x: number,
  y: number,
  rawWidth: number,
  rawHeight: number,
  orientation: string
): Point => {
  switch (orientation) {
    case "right":
      return { x: rawHeight - y, y: x };
    case "down":
      return { x: rawWidth - x, y: rawHeight - y };
    case "left":
      return { x: y, y: rawWidth - x };
    default:
      return { x, y };
  }
};

/** Inverse of transformPoint — converts display coords back to raw image coordinates. */
export const inverseTransformPoint = (
  x: number,
  y: number,
  rawWidth: number,
  rawHeight: number,
  orientation: string
): Point => {
  switch (orientation) {
    case "right":
      return { x: y, y: rawHeight - x };
    case "down":
      return { x: rawWidth - x, y: rawHeight - y };
    case "left":
      return { x: rawWidth - y, y: x };
    default:
      return { x, y };
  }
};
